import { api } from 'src/client/api';
import { selectIsAuthenticated } from 'src/common/selectors/auth';
import { selectIsFord, selectTuneInUserSerial } from '../selectors/app';
import { selectUserAccountId, selectUserEmail, selectUsername } from '../selectors/me';

export const FETCH_ME = 'FETCH_ME';
export const FETCH_ME_WITH_FAVORITES = 'FETCH_ME_WITH_FAVORITES';
export const FETCH_FAVORITES_FOLDER = 'FETCH_FAVORITES_FOLDER';
export const FETCH_MORE_FAVORITES_FOLDER_ITEMS = 'FETCH_MORE_FAVORITES_FOLDER_ITEMS';
export const REORDER_FAVORITES = 'REORDER_FAVORITES';
export const MOVE_FAVORITE_TO_FOLDER = 'MOVE_FAVORITE_TO_FOLDER';
export const ADD_FOLDER = 'ADD_FOLDER';
export const SET_DEFAULT = 'SET_DEFAULT';
export const UPDATE_USER = 'UPDATE_USER';
export const ADD_USER_CONSENT = 'ADD_USER_CONSENT';

export function selectFolder({ folder }) {
  const { contentId, folderId } = folder;

  return {
    type: FETCH_FAVORITES_FOLDER,
    api: {
      endpoint: ['user', 'getUserFolder'],
      args: [{ contentId, folderId }],
    },
    meta: {
      folder,
    },
  };
}

export function fetchMoreFolderItems({ folder }) {
  return (dispatch) => {
    const { contentId, folderId, nextOffset } = folder;

    return dispatch({
      type: FETCH_MORE_FAVORITES_FOLDER_ITEMS,
      payload: api.user.getUserFolder({ contentId, folderId, offset: nextOffset }),
      meta: {
        folder,
      },
    });
  };
}

export function reorderFavorites({ guideItem, fromIndex, toIndex, folder }) {
  return {
    type: REORDER_FAVORITES,
    api: {
      endpoint: ['user', 'orderFavorite'],
      args: [
        {
          guideId: guideItem.actions.follow.favoriteId,
          index: toIndex + 1, // Favs API is 1-based
        },
      ],
    },
    meta: {
      guideItem,
      folder,
      fromIndex,
      toIndex,
    },
  };
}

export function moveFavoriteToFolder({ guideItem, fromIndex, fromFolder, toFolder }) {
  return {
    type: MOVE_FAVORITE_TO_FOLDER,
    api: {
      endpoint: ['user', 'moveFavorite'],
      args: [
        {
          guideId: guideItem.actions.editFavorite.favoriteId,
          folderId: toFolder.folderId,
        },
      ],
    },
    meta: {
      guideItem,
      fromIndex,
      fromFolder,
      toFolder,
    },
  };
}

export function addFolder({ name, userId }) {
  return {
    type: ADD_FOLDER,
    api: {
      endpoint: ['user', 'addFolder'],
      args: [name],
    },
    meta: {
      name,
      userId,
    },
  };
}

export function setDefaultFolder({ folder }) {
  return {
    type: SET_DEFAULT,
    api: {
      endpoint: ['user', 'setDefaultFolder'],
      args: [folder.folderId],
    },
    meta: { folder },
  };
}

export function fetchMeWithFavorites() {
  return (dispatch, getState) => {
    const state = getState();

    if (selectIsAuthenticated(state)) {
      return dispatch({
        type: FETCH_ME_WITH_FAVORITES,
        api: {
          endpoint: ['user', 'getCurrentUserFavorites'],
          // TODO: this transform won't be needed when migrating to Zustand
          transform: (response) => ({ content: response }),
        },
        meta: {
          contentId: 'details',
        },
      });
    }

    return null;
  };
}

export function fetchMe(forceFetch = false) {
  return (dispatch, getState) => {
    const state = getState();

    if (
      forceFetch === true ||
      (selectIsAuthenticated(state) && !selectIsFord(state) && !selectUserAccountId(state))
    ) {
      return dispatch({
        type: FETCH_ME,
        api: {
          endpoint: ['user', 'get'],
          // TODO: this transform won't be needed when migrating to Zustand
          transform: (response) => ({ content: response }),
        },
        meta: {
          contentId: 'details',
        },
      });
    }

    return null;
  };
}

export function refreshMe() {
  return (dispatch, getState) => {
    const state = getState();

    if (selectIsAuthenticated(state)) {
      return dispatch({
        type: FETCH_ME,
        api: {
          endpoint: ['user', 'get'],
          // TODO: this transform won't be needed when migrating to Zustand
          transform: (response) => ({ content: response }),
        },
        meta: {
          contentId: 'details',
        },
      });
    }

    // need to return promise so we can wait for this to complete
    // and use a then
    return Promise.resolve();
  };
}

export function moveItemToNewFolder({ baseParams }) {
  return async (dispatch, getState) => {
    const res = await dispatch(addFolder(baseParams.addFolder));

    const {
      value: { data: folderId },
    } = res;
    const toFolder = getState().me.details.customFavoriteFoldersIndexed[folderId];
    const params = {
      ...baseParams.moveFavoriteToFolder,
      toFolder,
    };

    await dispatch(moveFavoriteToFolder(params));
  };
}

export function updateUser({ name, email, isFollowingListPublic }) {
  return {
    type: UPDATE_USER,
    api: {
      endpoint: ['user', 'update'],
      args: [{ name, email, isFollowingListPublic }],
      // TODO: this transform won't be needed when migrating to Zustand
      transform: (response) => ({ content: response }),
    },
    meta: {
      contentId: 'details',
    },
  };
}

export const addUserConsent =
  ({ type, version }) =>
  (dispatch, getState) => {
    const state = getState();

    const consent = {
      userId: selectTuneInUserSerial(state),
      userName: selectUsername(state),
      userEmail: selectUserEmail(state),
      agreementName: type,
      agreementVersion: version,
      acceptanceDate: new Date().toISOString(),
    };

    return dispatch({
      type: ADD_USER_CONSENT,
      api: {
        endpoint: ['user', 'addConsent'],
        args: [consent],
      },
      meta: {
        contentId: 'details',
      },
    });
  };
